import React, { useState, useEffect } from 'react';
import { Box, Typography, Container, Paper, Grid, Avatar, Menu, MenuItem, IconButton, Button, TextField, Dialog, DialogTitle, DialogContent, DialogActions, FormControl, Select } from '@mui/material';
import { styled } from '@mui/material/styles';
import supabase from '../supabaseClient';
import logo from '../assets/logo0.png';
import { Link } from '@mui/material';
import { AccountCircle, ConfirmationNumber, ShoppingBag, Settings, Logout, Edit as EditIcon, Visibility as ViewIcon } from '@mui/icons-material';
import { Divider } from '@mui/material';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';

// Styled components
const SupportContainer = styled(Box)({
  minHeight: '100vh',
  backgroundColor: '#2b2b2b',
  color: '#fff',
  paddingTop: '64px', // Height of navbar
});

const ServiceBanner = styled(Box)({
  padding: '3rem 0',
  backgroundColor: '#1a1a1a',
  borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
});

const ContentSection = styled(Box)({
  padding: '2rem 0',
  backgroundColor: '#2b2b2b',
});

const Logo = styled('img')({
  height: '30px',
  marginRight: '1rem',
  filter: 'brightness(0) invert(1)',
  cursor: 'pointer',
});

const TicketCard = styled(Paper)({
  padding: '1rem',
  marginBottom: '1rem',
  backgroundColor: '#1a1a1a',
  color: '#fff',
  '&:hover': {
    transform: 'translateY(-2px)',
    transition: 'transform 0.2s',
  },
});

const Header = styled(Box)({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: '1rem 2rem',
  backgroundColor: '#1a1a1a',
  borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
});

const SupportCard = styled(Paper)({
  padding: '2rem',
  height: '100%',
  backgroundColor: '#1a1a1a',
  color: '#fff',
  transition: 'all 0.3s ease',
  cursor: 'pointer',
  '&:hover': {
    transform: 'translateY(-8px)',
    boxShadow: '0 12px 20px rgba(0, 0, 0, 0.2)',
    backgroundColor: '#222',
  },
});

const SupportIcon = styled(Box)({
  width: '60px',
  height: '60px',
  borderRadius: '50%',
  backgroundColor: '#FFD700',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  marginBottom: '1.5rem',
});

const Footer = styled(Box)({
  backgroundColor: '#1a1a1a',
  color: '#fff',
  padding: '2rem 0',
  marginTop: '4rem',
  borderTop: '1px solid rgba(255, 255, 255, 0.1)',
});

const StyledMenuItem = styled(MenuItem)(({ theme }) => ({
  padding: '10px 20px',
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
  '&:hover': {
    backgroundColor: 'rgba(255, 215, 0, 0.1)',
  },
  '& .MuiSvgIcon-root': {
    fontSize: 20,
    color: '#FFD700',
  },
}));

const Support = () => {
  const [tickets, setTickets] = useState([]);
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);
  const [openTicketDialog, setOpenTicketDialog] = useState(false);
  const [newTicket, setNewTicket] = useState({
    title: '',
    description: '',
    department: 'general',
    priority: 'medium'
  });
  const [userProfile, setUserProfile] = useState({
    name: '',
    email: '',
    activeTickets: 0,
    pendingOrders: 0,
  });
  const [editTicketDialog, setEditTicketDialog] = useState(false);
  const [currentTicket, setCurrentTicket] = useState(null);
  const [editedDescription, setEditedDescription] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    fetchUserProfile();
    fetchTickets();
  }, []);

  const fetchUserProfile = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        console.error('No user found');
        return;
      }

      const { data: activeTickets, error: ticketsError } = await supabase
        .from('tickets')
        .select('count')
        .eq('user_id', user.id)
        .eq('status', 'open');

      const { data: pendingOrders, error: ordersError } = await supabase
        .from('orders')
        .select('count')
        .eq('user_id', user.id)
        .eq('status', 'pending');

      if (ticketsError) console.error('Error fetching tickets:', ticketsError);
      if (ordersError) console.error('Error fetching orders:', ordersError);

      setUserProfile({
        name: user.user_metadata?.full_name || user.email.split('@')[0],
        email: user.email,
        activeTickets: activeTickets?.[0]?.count || 0,
        pendingOrders: pendingOrders?.[0]?.count || 0,
      });

    } catch (error) {
      console.error('Error fetching user profile:', error);
    }
  };

  const fetchTickets = async () => {
    try {
      console.log('Attempting to fetch tickets...');

      if (!supabase) {
        console.error('Supabase client is not initialized');
        return;
      }

      const { data, error } = await supabase
        .from('tickets')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching tickets:', error);
        return;
      }

      console.log('Successfully fetched tickets:', data);
      setTickets(data || []);

    } catch (error) {
      console.error('Fetch error details:', error);
    }
  };

  const handleSubmitTicket = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        alert('Please login to submit a ticket');
        return;
      }

      // Validate input
      if (!newTicket.title || !newTicket.description) {
        alert('Please fill in all required fields');
        return;
      }

      // Check if supabase client is initialized
      if (!supabase) {
        console.error('Supabase client is not initialized');
        alert('Database connection error');
        return;
      }

      // Add timestamp and status
      const ticketData = {
        ...newTicket,
        status: 'open',
        created_at: new Date().toISOString(),
        user_id: user.id,
      };

      console.log('Submitting ticket data:', ticketData);

      // Remove .single() as it might cause issues if the database doesn't return data
      const { error } = await supabase
        .from('tickets')
        .insert(ticketData);

      if (error) {
        console.error('Supabase error details:', error);
        throw new Error(error.message || 'Failed to submit ticket');
      }

      // Success case
      console.log('Ticket submitted successfully');

      // Clear form and close dialog
      setNewTicket({
        title: '',
        description: '',
        department: 'general',
        priority: 'medium'
      });
      setOpenTicketDialog(false);

      // Refresh tickets list
      await fetchTickets();

      // After successful submission, refresh user profile to update ticket count
      await fetchUserProfile();

    } catch (error) {
      console.error('Submission error details:', error);
      alert(error.message || 'Failed to submit ticket. Please try again.');
    }
  };

  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  // Add this useEffect to verify Supabase connection on component mount
  useEffect(() => {
    const checkSupabaseConnection = async () => {
      try {
        const { data, error } = await supabase.from('tickets').select('count');
        if (error) {
          console.error('Supabase connection error:', error);
        } else {
          console.log('Supabase connection successful');
        }
      } catch (error) {
        console.error('Connection test error:', error);
      }
    };

    checkSupabaseConnection();
  }, []);

  const handleEditClick = (ticket) => {
    setCurrentTicket(ticket);
    setEditedDescription(ticket.description);
    setEditTicketDialog(true);
  };

  const handleUpdateTicket = async () => {
    try {
      const { error } = await supabase
        .from('tickets')
        .update({ description: editedDescription })
        .eq('id', currentTicket.id);

      if (error) throw error;

      // Update local state
      setTickets(tickets.map(ticket => 
        ticket.id === currentTicket.id 
          ? { ...ticket, description: editedDescription }
          : ticket
      ));

      setEditTicketDialog(false);
      setCurrentTicket(null);
      setEditedDescription('');

    } catch (error) {
      console.error('Error updating ticket:', error);
      alert('Failed to update ticket');
    }
  };

  const handleViewTicket = (ticketId) => {
    navigate(`/ticket/${ticketId}`);
  };

  return (
    <SupportContainer>
      {/* Updated Header */}
      <Header>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Logo 
            src={logo} 
            alt="Domain Factory" 
            onClick={() => navigate('/dashboard')}
            style={{ cursor: 'pointer' }} 
          />
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          {/* Enhanced Customer Menu Button */}
          <Button
            sx={{ 
              color: '#fff',
              textTransform: 'none',
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              padding: '8px 16px',
              borderRadius: '8px',
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
              }
            }}
            onClick={handleMenuOpen}
            endIcon={<Avatar 
              sx={{ 
                bgcolor: '#1e1e1e', 
                width: 32, 
                height: 32,
                border: '2px solid #FFD700'
              }}
            >
              {userProfile.name.charAt(0)}
            </Avatar>}
          >
            <Typography sx={{ fontWeight: 500 }}>
              Customer Menu
            </Typography>
          </Button>

          {/* Enhanced Profile Menu */}
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleMenuClose}
            PaperProps={{
              sx: {
                mt: 1.5,
                minWidth: 280,
                backgroundColor: '#1a1a1a',
                color: '#fff',
                boxShadow: '0 8px 16px rgba(0,0,0,0.3)',
                '& .MuiList-root': {
                  padding: '8px 0',
                },
              },
            }}
            transformOrigin={{ horizontal: 'right', vertical: 'top' }}
            anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
          >
            {/* Profile Header */}
            <Box sx={{ p: 2, borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                <Avatar 
                  sx={{ 
                    width: 48, 
                    height: 48,
                    bgcolor: '#FFD700',
                    color: '#000',
                    fontWeight: 'bold'
                  }}
                >
                  {userProfile.name.charAt(0)}
                </Avatar>
                <Box>
                  <Typography sx={{ fontWeight: 'bold' }}>{userProfile.name}</Typography>
                  <Typography variant="body2" sx={{ opacity: 0.7 }}>{userProfile.email}</Typography>
                </Box>
              </Box>
            </Box>

            {/* Quick Stats */}
            <Box sx={{ p: 2, backgroundColor: 'rgba(255,255,255,0.05)' }}>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="h6" sx={{ color: '#FFD700' }}>{userProfile.activeTickets}</Typography>
                    <Typography variant="body2" sx={{ opacity: 0.7 }}>Active Tickets</Typography>
                  </Box>
                </Grid>
                <Grid item xs={6}>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="h6" sx={{ color: '#FFD700' }}>{userProfile.pendingOrders}</Typography>
                    <Typography variant="body2" sx={{ opacity: 0.7 }}>Pending Orders</Typography>
                  </Box>
                </Grid>
              </Grid>
            </Box>

            {/* Menu Items */}
            <StyledMenuItem component={RouterLink} to="/profile">
              <AccountCircle />
              <Typography>My Profile</Typography>
            </StyledMenuItem>
            
            <StyledMenuItem component={RouterLink} to="/support">
              <ConfirmationNumber />
              <Box sx={{ flex: 1 }}>
                <Typography>Support Tickets</Typography>
                {userProfile.activeTickets > 0 && (
                  <Typography variant="caption" sx={{ color: '#FFD700' }}>
                    {userProfile.activeTickets} active tickets
                  </Typography>
                )}
              </Box>
            </StyledMenuItem>
            
            <StyledMenuItem component={RouterLink} to="/orders">
              <ShoppingBag />
              <Box sx={{ flex: 1 }}>
                <Typography>My Orders</Typography>
                {userProfile.pendingOrders > 0 && (
                  <Typography variant="caption" sx={{ color: '#FFD700' }}>
                    {userProfile.pendingOrders} pending orders
                  </Typography>
                )}
              </Box>
            </StyledMenuItem>

            <Divider sx={{ my: 1, borderColor: 'rgba(255,255,255,0.1)' }} />
            
            <StyledMenuItem component={RouterLink} to="/settings">
              <Settings />
              <Typography>Settings</Typography>
            </StyledMenuItem>
            
            <StyledMenuItem sx={{ color: '#ff4444' }}>
              <Logout />
              <Typography>Logout</Typography>
            </StyledMenuItem>
          </Menu>
        </Box>
      </Header>

      {/* Service Banner with updated styling */}
      <ServiceBanner>
        <Container maxWidth="lg">
          <Typography
            variant="h5"
            sx={{
              fontWeight: 500,
              mb: 2,
              color: '#fff',
              fontFamily: 'Arial, sans-serif',
            }}
          >
            One.Done!© Express Service: One Click and Done.
          </Typography>
          
          <Typography
            sx={{
              fontSize: '1rem',
              lineHeight: 1.6,
              color: '#fff',
              opacity: 0.8,
              maxWidth: '800px',
            }}
          >
            To ensure you receive the best possible support from us, we recommend our exclusive One.Done!© Express Service: One Click and Done. Your request immediately reaches the department you specify. Thanks to using this service through the customer menu, your request is considered authenticated. You will receive our response via email.
          </Typography>
        </Container>
      </ServiceBanner>

      {/* Support Content Section */}
      <ContentSection>
        <Container maxWidth="lg">
          <Paper
            sx={{
              backgroundColor: '#fff',
              borderRadius: '8px',
              overflow: 'hidden',
              mb: 4,
            }}
          >
            <Box sx={{ p: 3 }}>
              <Typography variant="h6" sx={{ color: '#333', mb: 2 }}>
                Your Open Support Requests
              </Typography>
              <Typography sx={{ color: '#666' }}>
                Due to the upgrade of our internal ticket system, the display of your previous tickets is no longer available in this area.
              </Typography>
              <Button
                variant="contained"
                sx={{
                  mt: 3,
                  bgcolor: '#00693C',
                  color: '#fff',
                  '&:hover': {
                    bgcolor: '#005732',
                  },
                  textTransform: 'none',
                  padding: '8px 24px',
                }}
                onClick={() => setOpenTicketDialog(true)}
              >
                Create New Support Request
              </Button>
            </Box>
          </Paper>
        </Container>
      </ContentSection>

      {/* Ticket Submission Dialog */}
      <Dialog 
        open={openTicketDialog} 
        onClose={() => setOpenTicketDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Create New Support Ticket</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
            <TextField
              label="Title"
              fullWidth
              value={newTicket.title}
              onChange={(e) => setNewTicket({ ...newTicket, title: e.target.value })}
            />
            
            <FormControl fullWidth>
              <Select
                value={newTicket.department}
                onChange={(e) => setNewTicket({ ...newTicket, department: e.target.value })}
                label="Department"
              >
                <MenuItem value="general">General</MenuItem>
                <MenuItem value="technical">Technical Support</MenuItem>
                <MenuItem value="billing">Billing</MenuItem>
                <MenuItem value="other">Other</MenuItem>
              </Select>
            </FormControl>

            <FormControl fullWidth>
              <Select
                value={newTicket.priority}
                onChange={(e) => setNewTicket({ ...newTicket, priority: e.target.value })}
                label="Priority"
              >
                <MenuItem value="low">Low</MenuItem>
                <MenuItem value="medium">Medium</MenuItem>
                <MenuItem value="high">High</MenuItem>
              </Select>
            </FormControl>

            <TextField
              label="Description"
              multiline
              rows={4}
              fullWidth
              value={newTicket.description}
              onChange={(e) => setNewTicket({ ...newTicket, description: e.target.value })}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenTicketDialog(false)}>Cancel</Button>
          <Button 
            onClick={handleSubmitTicket}
            variant="contained"
            sx={{ bgcolor: '#FFD700', color: '#000' }}
          >
            Create Ticket
          </Button>
        </DialogActions>
      </Dialog>

      {/* Tickets List */}
      <Paper
        sx={{
          backgroundColor: '#fff',
          padding: '2rem',
          borderRadius: '8px',
          mb: 4,
        }}
      >
        <Typography variant="h6" sx={{ color: '#333', mb: 3 }}>
          Your Support Tickets
        </Typography>
        
        {tickets.length > 0 ? (
          tickets.map((ticket) => (
            <TicketCard key={ticket.id} elevation={2}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="h6" sx={{ 
                    color: '#FFD700', 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: 1 
                  }}>
                    {ticket.title}
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <IconButton 
                        size="small" 
                        onClick={() => handleViewTicket(ticket.id)}
                        sx={{ 
                          color: '#FFD700',
                          '&:hover': { backgroundColor: 'rgba(255, 215, 0, 0.1)' }
                        }}
                      >
                        <ViewIcon fontSize="small" />
                      </IconButton>
                      <IconButton 
                        size="small" 
                        onClick={() => handleEditClick(ticket)}
                        sx={{ 
                          color: '#FFD700',
                          '&:hover': { backgroundColor: 'rgba(255, 215, 0, 0.1)' }
                        }}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 2, mb: 1 }}>
                    <Typography sx={{ 
                      opacity: 0.8,
                      fontSize: '0.875rem',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.5 
                    }}>
                      Status: {ticket.status}
                    </Typography>
                    <Typography sx={{ 
                      opacity: 0.8,
                      fontSize: '0.875rem'
                    }}>
                      Created: {format(new Date(ticket.created_at), 'MMM dd, yyyy HH:mm')}
                    </Typography>
                  </Box>
                  <Typography variant="body2">
                    {ticket.description}
                  </Typography>
                </Box>
              </Box>
            </TicketCard>
          ))
        ) : (
          <Typography sx={{ color: '#666' }}>
            No active tickets available.
          </Typography>
        )}
      </Paper>

      {/* Add this Dialog component for editing */}
      <Dialog 
        open={editTicketDialog} 
        onClose={() => setEditTicketDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Edit Ticket: {currentTicket?.title}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <ReactQuill 
              value={editedDescription}
              onChange={setEditedDescription}
              theme="snow"
              modules={{
                toolbar: [
                  [{ 'header': [1, 2, false] }],
                  ['bold', 'italic', 'underline', 'strike', 'blockquote'],
                  [{'list': 'ordered'}, {'list': 'bullet'}],
                  ['link'],
                  ['clean']
                ],
              }}
              formats={[
                'header',
                'bold', 'italic', 'underline', 'strike', 'blockquote',
                'list', 'bullet',
                'link'
              ]}
              style={{
                height: '200px',
                marginBottom: '50px',
                backgroundColor: '#fff'
              }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditTicketDialog(false)}>
            Cancel
          </Button>
          <Button 
            onClick={handleUpdateTicket}
            variant="contained"
            sx={{ 
              bgcolor: '#FFD700', 
              color: '#000',
              '&:hover': {
                bgcolor: '#E5C100'
              }
            }}
          >
            Update Ticket
          </Button>
        </DialogActions>
      </Dialog>

      {/* Enhanced Support Features */}
      <Box sx={{ py: 6, px: 2 }}>
        <Container maxWidth="lg">
          <Grid container spacing={4}>
            <Grid item xs={12} md={4}>
              <SupportCard elevation={3}>
                <SupportIcon>
                  <Typography variant="h4" sx={{ color: '#000', fontWeight: 'bold' }}>?</Typography>
                </SupportIcon>
                <Typography variant="h6" sx={{ 
                  mb: 2, 
                  color: '#FFD700',
                  fontWeight: 'bold',
                  fontSize: '1.25rem'
                }}>
                  FAQ & Knowledge Base
                </Typography>
                <Typography sx={{ 
                  color: '#fff',
                  opacity: 0.9,
                  lineHeight: 1.6
                }}>
                  Find answers to common questions and detailed documentation in our comprehensive knowledge base.
                </Typography>
                <Button
                  variant="text"
                  sx={{
                    color: '#FFD700',
                    mt: 2,
                    '&:hover': {
                      backgroundColor: 'rgba(255, 215, 0, 0.1)',
                    }
                  }}
                >
                  Learn More →
                </Button>
              </SupportCard>
            </Grid>

            <Grid item xs={12} md={4}>
              <SupportCard elevation={3}>
                <SupportIcon>
                  <Typography variant="h4" sx={{ color: '#000', fontWeight: 'bold' }}>💬</Typography>
                </SupportIcon>
                <Typography variant="h6" sx={{ 
                  mb: 2, 
                  color: '#FFD700',
                  fontWeight: 'bold',
                  fontSize: '1.25rem'
                }}>
                  Live Chat Support
                </Typography>
                <Typography sx={{ 
                  color: '#fff',
                  opacity: 0.9,
                  lineHeight: 1.6
                }}>
                  Connect with our support team in real-time for immediate assistance with your questions.
                </Typography>
                <Button
                  variant="text"
                  sx={{
                    color: '#FFD700',
                    mt: 2,
                    '&:hover': {
                      backgroundColor: 'rgba(255, 215, 0, 0.1)',
                    }
                  }}
                >
                  Start Chat →
                </Button>
              </SupportCard>
            </Grid>

            <Grid item xs={12} md={4}>
              <SupportCard elevation={3}>
                <SupportIcon>
                  <Typography variant="h4" sx={{ color: '#000', fontWeight: 'bold' }}>✉️</Typography>
                </SupportIcon>
                <Typography variant="h6" sx={{ 
                  mb: 2, 
                  color: '#FFD700',
                  fontWeight: 'bold',
                  fontSize: '1.25rem'
                }}>
                  Email Support
                </Typography>
                <Typography sx={{ 
                  color: '#fff',
                  opacity: 0.9,
                  lineHeight: 1.6
                }}>
                  Submit detailed inquiries through our email support system for thorough assistance.
                </Typography>
                <Button
                  variant="text"
                  sx={{
                    color: '#FFD700',
                    mt: 2,
                    '&:hover': {
                      backgroundColor: 'rgba(255, 215, 0, 0.1)',
                    }
                  }}
                >
                  Contact Us →
                </Button>
              </SupportCard>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Footer */}
      <Footer>
        <Container maxWidth="lg">
          <Grid container spacing={4}>
            <Grid item xs={12} md={4}>
              <Box sx={{ mb: 3 }}>
                <Logo src={logo} alt="Domain Factory" sx={{ height: '30px', mb: 2 }} />
                <Typography sx={{ opacity: 0.7, fontSize: '0.9rem' }}>
                  Your trusted partner for domain and hosting solutions.
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} md={2}>
              <Typography variant="h6" sx={{ mb: 2, fontSize: '1rem' }}>Support</Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Link href="#" sx={{ color: '#fff', opacity: 0.7, textDecoration: 'none', '&:hover': { opacity: 1 } }}>Help Center</Link>
                <Link href="#" sx={{ color: '#fff', opacity: 0.7, textDecoration: 'none', '&:hover': { opacity: 1 } }}>Contact Us</Link>
                <Link href="#" sx={{ color: '#fff', opacity: 0.7, textDecoration: 'none', '&:hover': { opacity: 1 } }}>Status</Link>
              </Box>
            </Grid>
            <Grid item xs={12} md={2}>
              <Typography variant="h6" sx={{ mb: 2, fontSize: '1rem' }}>Company</Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Link href="#" sx={{ color: '#fff', opacity: 0.7, textDecoration: 'none', '&:hover': { opacity: 1 } }}>About Us</Link>
                <Link href="#" sx={{ color: '#fff', opacity: 0.7, textDecoration: 'none', '&:hover': { opacity: 1 } }}>Blog</Link>
                <Link href="#" sx={{ color: '#fff', opacity: 0.7, textDecoration: 'none', '&:hover': { opacity: 1 } }}>Careers</Link>
              </Box>
            </Grid>
            <Grid item xs={12} md={4}>
              <Typography variant="h6" sx={{ mb: 2, fontSize: '1rem' }}>Newsletter</Typography>
              <Typography sx={{ opacity: 0.7, mb: 2 }}>
                Stay updated with our latest features and releases.
              </Typography>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <TextField 
                  size="small"
                  placeholder="Enter your email"
                  sx={{ 
                    backgroundColor: '#fff',
                    borderRadius: 1,
                    '& .MuiOutlinedInput-root': {
                      '& fieldset': {
                        borderColor: 'transparent',
                      },
                    },
                  }}
                />
                <Button 
                  variant="contained"
                  sx={{ 
                    bgcolor: '#FFD700',
                    color: '#000',
                    '&:hover': {
                      bgcolor: '#E5C100',
                    },
                  }}
                >
                  Subscribe
                </Button>
              </Box>
            </Grid>
          </Grid>
          <Box sx={{ mt: 4, pt: 4, borderTop: '1px solid rgba(255, 255, 255, 0.1)', textAlign: 'center' }}>
            <Typography sx={{ opacity: 0.7, fontSize: '0.9rem' }}>
              © 2024 Domain Factory. All rights reserved.
            </Typography>
          </Box>
        </Container>
      </Footer>
    </SupportContainer>
  );
};

export default Support; 